import { useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import {
  findPushPlatform,
  type PushApiError,
  type PushErrorType,
  type PushPlatformDetails,
} from '@/features/push-credentials';
import { pushErrorsOrFallback, SANDBOX_FAILED_MESSAGE } from '../utils/push-errors';
import { useSandboxSwitchConfirmation } from './push-confirmations';
import type { PushCredentialWrites } from './push-credential-writes';

function savedSandboxFor(platforms: readonly PushPlatformDetails[]): boolean {
  return findPushPlatform(platforms, 'ios')?.development ?? false;
}

export type UseApnsSandboxOptions = {
  platforms: readonly PushPlatformDetails[];
  isEnabled: boolean;
  isEditing: boolean;
  writes: PushCredentialWrites;
  addErrors: (type: PushErrorType, errors: readonly PushApiError[]) => void;
  onEditingChange: (isEditing: boolean) => void;
};

export type ApnsSandboxState = {
  sandbox: boolean;
  savedSandbox: boolean;
  sendsToDevelopment: boolean;
  changeSandbox: (checked: boolean) => void;
  resetSandbox: () => void;
};

/**
 * A stored credential's endpoint is live: flipping it redirects every message
 * already going out, so it is confirmed first and written on its own, not on save.
 */
export function useApnsSandbox({
  platforms,
  isEnabled,
  isEditing,
  writes,
  addErrors,
  onEditingChange,
}: UseApnsSandboxOptions): ApnsSandboxState {
  const snackbars = useSnackbars();
  const savedSandbox = savedSandboxFor(platforms);
  const [sandbox, setSandbox] = useState(savedSandbox);

  const openConfirmation = useSandboxSwitchConfirmation({
    isSandbox: sandbox,
    onSwitch: async (next) => {
      try {
        await writes.setConfig('push_apns', next ? 'sandbox' : '');
      } catch (error) {
        addErrors('config', pushErrorsOrFallback(error, SANDBOX_FAILED_MESSAGE));
        return;
      }
      snackbars.create({
        variant: 'default',
        id: 'push.apns.sandbox',
        message: `Sandbox environment ${next ? 'enabled' : 'disabled'}.`,
      });
      onEditingChange(false);
      setSandbox(next);
    },
  });

  const isTyped = !isEnabled || isEditing;

  const changeSandbox = (checked: boolean) => {
    if (isTyped) {
      setSandbox(checked);
      return;
    }
    openConfirmation();
  };

  return {
    sandbox,
    savedSandbox,
    sendsToDevelopment: sandbox && savedSandbox,
    changeSandbox,
    resetSandbox: () => setSandbox(savedSandbox),
  };
}
